import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FlexV2 from '@/primitives/FlexV2'
import Icon from '@/primitives/Icon'
import Text from '@/primitives/Text'
import Button from '@/primitives/Button'
import Strings from '../../Strings'
import SensorFilterDialog from './SensorFilterDialog'
import DeviceFilterButton from './DeviceFilterButton'
import DateFilterButton from './DateFilter/components/DateFilterButton'
import DateFilterDialog from './DateFilter/components/DateFilterDialog'
import { getTrialState } from '../../utils'
import { setEnvSensors, setSoilSensors } from '@/slices/management/trial'
const MetricsHeader = ({
  type,
  viewMode,
  showDelta,
  onDeltaToggle,
  onViewModeChange,
  sectionAId,
  sectionBId,
  isSmallScreen
}) => {
  const strings = Strings()
  const dispatch = useDispatch()
  const [isSensorDialogOpen, setSensorDialogOpen] = useState(false)
  const [isDateDialogOpen, setDateDialogOpen] = useState(false)
  const [overflowActive, setOverflowActive] = useState(false)
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false)

  const controlsContainerRef = useRef(null)
  const buttonsRef = useRef([])

  const {
    envSensorsA,
    envSensorsB,
    soilSensorsA,
    soilSensorsB,
    dateFilterEnv,
    dateFilterSoil,
    dateFiltersSynced
  } = useSelector(getTrialState)

  const isEnvironmental = type === 'envirosense'

  const hasSensorFilters = isEnvironmental
    ? envSensorsA.length > 0 || envSensorsB.length > 0
    : soilSensorsA.length > 0 || soilSensorsB.length > 0

  const handleClearSensorFilters = () => {
    if (isEnvironmental) {
      dispatch(
        setEnvSensors({
          sectionA: [],
          sectionB: []
        })
      )
    } else {
      dispatch(
        setSoilSensors({
          sectionA: [],
          sectionB: []
        })
      )
    }
  }

  useEffect(() => {
    const checkOverflow = () => {
      if (!controlsContainerRef.current || isSmallScreen) return

      const containerWidth = controlsContainerRef.current.offsetWidth
      let totalButtonsWidth = 0

      buttonsRef.current.forEach(buttonRef => {
        if (buttonRef) {
          totalButtonsWidth += buttonRef.offsetWidth
        }
      })

      totalButtonsWidth += (buttonsRef.current.length - 1) * 16

      setOverflowActive(totalButtonsWidth > containerWidth)
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [isSmallScreen, viewMode])

  const title =
    strings[`${type === 'envirosense' ? 'environmental' : 'soil'}MetricsTitle`]

  const titleSize = isSmallScreen ? 300 : 400

  const renderControls = () => {
    const viewModeButtons = (
      <FlexV2 ref={el => (buttonsRef.current[0] = el)} key='viewModeToggle'>
        <Button
          variant={viewMode === 'live' ? 'primary' : 'outline'}
          onClick={() => onViewModeChange('live')}
        >
          {strings.liveData}
        </Button>
        <Button
          variant={viewMode === 'historical' ? 'primary' : 'outline'}
          onClick={() => onViewModeChange('historical')}
        >
          {strings.historical}
        </Button>
      </FlexV2>
    )

    const deviceFilterButton = (
      <div ref={el => (buttonsRef.current[1] = el)} key='deviceFilter'>
        <DeviceFilterButton
          hasActiveFilters={hasSensorFilters}
          onClick={() => setSensorDialogOpen(true)}
          onClearFilters={handleClearSensorFilters}
          sensorType={type}
        />
      </div>
    )

    const dateFilterButton =
      viewMode === 'historical' ? (
        <div ref={el => (buttonsRef.current[2] = el)} key='dateFilter'>
          <DateFilterButton
            onClick={() => setDateDialogOpen(true)}
            currentDateFilter={
              type === 'envirosense' ? dateFilterEnv : dateFilterSoil
            }
            dateFiltersSynced={dateFiltersSynced}
          />
        </div>
      ) : null

    const deltaToggleButton =
      viewMode === 'historical' ? (
        <div ref={el => (buttonsRef.current[3] = el)} key='deltaToggle'>
          <Button
            variant='outline'
            iconBefore='compare_arrows'
            onClick={onDeltaToggle}
          >
            {showDelta ? strings.showSections : strings.showDelta}
          </Button>
        </div>
      ) : null

    if (!overflowActive || isSmallScreen) {
      return (
        <>
          {deviceFilterButton}
          {dateFilterButton}
          {deltaToggleButton}
          {viewModeButtons}
        </>
      )
    }

    return (
      <>
        <div ref={el => (buttonsRef.current[4] = el)} key='overflow'>
          <Button
            variant='outline'
            iconBefore='more_horiz'
            onClick={() => setOverflowMenuOpen(true)}
          >
            {strings.more || 'More'}
          </Button>
          {overflowMenuOpen && (
            <div className='overflow-menu'>
              <div className='overflow-menu-content'>
                {deviceFilterButton}
                {dateFilterButton}
                {deltaToggleButton}
                <Button
                  variant='outline'
                  onClick={() => setOverflowMenuOpen(false)}
                  iconBefore='close'
                >
                  {strings.close || 'Close'}
                </Button>
              </div>
            </div>
          )}
        </div>
        {viewModeButtons}
      </>
    )
  }

  return (
    <>
      <FlexV2 alignCrossAxis='center' axisGap={200}>
        <Icon
          name={isEnvironmental ? 'thermostat' : 'water_drop'}
          size={titleSize}
          variant='primary'
          tone={600}
        />
        <Text size={titleSize} fontWeight={600}>
          {title}
        </Text>
        {isSmallScreen && (
          <Text size={100} as='span' variant='neutral'>
            {strings.liveData}
          </Text>
        )}
      </FlexV2>

      {!isSmallScreen && (
        <FlexV2
          ref={controlsContainerRef}
          alignCrossAxis='center'
          alignMainAxis='flex-end'
          axisGap={400}
          wrap='wrap'
        >
          {renderControls()}
          <SensorFilterDialog
            open={isSensorDialogOpen}
            onOpenChange={setSensorDialogOpen}
            sectionAId={sectionAId}
            sectionBId={sectionBId}
            dialogMode={type}
          />

          <DateFilterDialog
            open={isDateDialogOpen}
            onOpenChange={setDateDialogOpen}
            dialogMode={type}
          />
        </FlexV2>
      )}
    </>
  )
}

export default MetricsHeader
